import React from 'react';
import { useCustomEventListener } from 'react-custom-events';
import { useHistory } from 'react-router-dom';

import mainPageConfig from '@client/config/mainPageConfig';
import { UxContext } from '@client/context/UxContext';
import useDateTimeFormatters from '@client/hooks/useDateTimeFormatters';
import { useGlobalStore } from '@client/store/globalStore/context/globalContext';
import i18n from '@client/utils/i18n';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import MenuIcon from '@mui/icons-material/Menu';
import { AppBar, Box, IconButton, Toolbar, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';

const useStyles = makeStyles((theme: Theme) => {
    const drawerWidth = 260;
    const transitionOptions = {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen
    };

    return {
        content: {
            marginLeft: '55px',
            marginTop: '50px'
        },
        root: {
            display: 'flex'
        },
        appBar: {
            zIndex: theme.zIndex.drawer + 1,
            transition: theme.transitions.create(['width', 'margin'], transitionOptions)
        },
        appBarShift: {
            marginLeft: drawerWidth,
            width: `calc(100% - ${drawerWidth}px)`,
            transition: theme.transitions.create(['width', 'margin'], transitionOptions)
        },
        menuButton: {
            marginRight: 36
        },
        hide: {
            display: 'none'
        },
        drawer: {
            width: drawerWidth,
            flexShrink: 0,
            whiteSpace: 'nowrap'
        },
        drawerOpen: {
            zIndex: 2000,
            width: drawerWidth,
            transition: theme.transitions.create('width', transitionOptions)
        },
        drawerClose: {
            overflowX: 'hidden',
            width: '55px',
            transition: theme.transitions.create('width', transitionOptions)
        },
        title: {
            paddingTop: 0,
            paddingBottom: 0,
            marginTop: '5px',
            transition: theme.transitions.create(['height', 'opacity'], transitionOptions)
        },
        titleOpen: {
            height: '35px',
            opacity: 1
        },
        titleClose: {
            height: 0,
            overflow: 'hidden',
            opacity: 0
        },
        toolbar: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            padding: theme.spacing(0, 1),
            ...theme.mixins.toolbar
        }
    };
});

interface HeaderProps {
    children: React.ReactNode;
}

function toTimeString(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

const Header = ({ children }: HeaderProps) => {
    const { handleSnackbar } = React.useContext(UxContext)!;
    const history = useHistory();
    const classes = useStyles({});
    const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
    const { formatTime, formatDate } = useDateTimeFormatters();
    const {
        globalState: { userData, dateTimeFormatLoaded, serverTimeOffset },
        globalActions
    } = useGlobalStore();
    const [now, setNow] = React.useState(() => new Date(Date.now() + serverTimeOffset));
    const ref = React.useRef<HTMLDivElement>(null);

    const dynClass = (name: string) =>
        clsx(classes[name as keyof typeof classes], {
            [classes[`${name}Open` as keyof typeof classes]]: isDrawerOpen,
            [classes[`${name}Close` as keyof typeof classes]]: !isDrawerOpen
        });
    let lastGroup: React.ReactNode;

    useCustomEventListener('lediumApiError', (data: string) => {
        handleSnackbar({
            message: data,
            severity: 'error'
        });
    });
    // const onSelectLanguage = (lang) => {
    //     window.lang = lang.toLocaleLowerCase();
    // }

    const onSelectPage = (page: string) => {
        history.push('/' + page);
        setIsDrawerOpen(false);
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (ref.current && !ref.current.contains(event.target as Node)) {
            setIsDrawerOpen(false);
        }
    };

    React.useEffect(() => {
        if (userData.status === 'LOGGED_OUT') {
            handleSnackbar({
                message: i18n('logout_successful') as string,
                severity: 'info'
            });
            history.push('/login');
        }
        if (userData.status === 'LOGGED_IN') {
            if (userData.auth === true) {
                handleSnackbar({
                    message: i18n('login_successful') as string,
                    severity: 'success'
                });
                history.push('/');
            } else {
                handleSnackbar({
                    message: i18n('login_failed') as string,
                    severity: 'error'
                });
            }
        }
    }, [userData.status]);

    function onClickLogout() {
        globalActions.users.callUserLogout();
    }

    React.useEffect(() => {
        document.addEventListener('click', handleClickOutside, true);

        return () => {
            document.removeEventListener('click', handleClickOutside, true);
        };
    }, []);

    React.useEffect(() => {
        const timer = setInterval(() => setNow(new Date(Date.now() + serverTimeOffset)), 1000);
        return () => clearInterval(timer);
    }, [serverTimeOffset]);

    // for login screen
    if (!userData.auth) {
        return <>{children}</>;
    }

    return (
        <>
            <AppBar position="fixed" className={clsx(classes.appBar, { [classes.appBarShift]: isDrawerOpen })}>
                <Toolbar>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        // eslint-disable-next-line
                    onClick={() => setIsDrawerOpen(true)}
                        edge="start"
                        className={clsx(classes.menuButton, { [classes.hide]: isDrawerOpen })}
                        size="large"
                    >
                        <MenuIcon />
                    </IconButton>
                    <Typography variant="h6" noWrap style={{ flexGrow: 1 }}>
                        {i18n('ledium_title')}
                    </Typography>
                    {dateTimeFormatLoaded && (
                        <Box
                            sx={{
                                position: 'absolute',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '6px'
                            }}
                        >
                            <Typography variant="body2" noWrap sx={{ lineHeight: 1 }}>
                                {formatDate(now.getTime())}
                            </Typography>
                            <Typography variant="body2" noWrap sx={{ lineHeight: 1 }}>
                                {formatTime(toTimeString(now))}
                            </Typography>
                        </Box>
                    )}
                    <Button color="inherit" onClick={onClickLogout}>
                        {i18n('logout_user', userData.username)}
                    </Button>
                    {/* <ReactFlagsSelect*/}
                    {/*    countries={availableLocalisations.map(e => e.toLocaleUpperCase())}*/}
                    {/*    defaultCountry="GB"*/}
                    {/*    showSelectedLabel={false}*/}
                    {/*    showOptionLabel={false}*/}
                    {/*    selectedSize={18}*/}
                    {/*    optionsSize={18}*/}
                    {/*    onSelect={onSelectLanguage}*/}
                    {/* />*/}
                </Toolbar>
            </AppBar>
            <Drawer variant="permanent" className={dynClass('drawer')} classes={{ paper: dynClass('drawer') }} ref={ref}>
                <div className={classes.toolbar}>
                    {/* eslint-disable-next-line */}
                <IconButton onClick={() => setIsDrawerOpen(false)} size="large">
                        <ChevronLeftIcon />
                    </IconButton>
                </div>
                <Divider />
                <List>
                    {Object.values(mainPageConfig).map((page, idx) => {
                        let showGroup = null;
                        if (page.group && page.group !== lastGroup) {
                            lastGroup = page.group;
                            showGroup = page.group;
                        }

                        return (
                            <React.Fragment key={`menu-${idx}`}>
                                {showGroup ? (
                                    <>
                                        <Divider />
                                        <ListItem className={dynClass('title')}>{showGroup}</ListItem>
                                    </>
                                ) : null}
                                <Tooltip
                                    placement="right"
                                    arrow
                                    title={
                                        <>
                                            {isDrawerOpen ? null : <Typography variant="subtitle2">{page.title}</Typography>}
                                            <Typography variant="body2">{page.description}</Typography>
                                        </>
                                    }
                                >
                                    {/* eslint-disable-next-line */}
                            <ListItem button onClick={() => onSelectPage(page.page)}>
                                        <ListItemIcon>{page.icon}</ListItemIcon>
                                        <ListItemText primary={page.title} />
                                    </ListItem>
                                </Tooltip>
                            </React.Fragment>
                        );
                    })}
                </List>
            </Drawer>
            <div className={classes.content}>{children}</div>
        </>
    );
};

export default Header;
